import { z } from "zod"

import type { BillingPlan, EffectiveEntitlement } from "./billing-core.ts"

/**
 * Anysola credits' pure half. A plan is a membership of every Anysola app,
 * and credits are how the apps meter it: each plan comes with an allowance
 * every month, and any app the account is signed in to spends from that one
 * balance. No database and no `server-only`, so `test/account/credits-core.test.ts`
 * can import it the way the other `*-core.ts` modules are tested.
 *
 * Two pools make up a balance:
 *
 * - The **allowance** is the plan's monthly amount. It isn't stored as a
 *   number that gets topped up — only what was *used* in each monthly cycle
 *   is stored, and the allowance is read from the account's plan at the
 *   moment of asking. Upgrading mid-month therefore raises what's left at
 *   once, a lapsed plan lowers it at once, and there's no job that has to run
 *   on the first of the month. Unused allowance doesn't carry over.
 * - **Bonus** credits are a stored balance that never expires: what an admin
 *   adds by hand today, and what a credit pack would add if we ever sell one.
 *
 * Spending takes from the allowance first and bonus second, so bonus credits
 * are only touched once the month's allowance is gone.
 */

/** The plans, in rank order. Kept here as a value so this module needs no runtime import. */
export const CREDIT_PLANS = ["free", "pro", "team"] as const satisfies readonly BillingPlan[]

/**
 * What each plan comes with until an admin sets its own numbers. Free starts
 * at zero so nothing is given away before anyone has decided it should be.
 */
export const DEFAULT_CREDIT_ALLOWANCES: Record<BillingPlan, number> = {
  free: 0,
  pro: 1_000,
  team: 1_000,
}

/**
 * The two balances an account holds. **credits** is what the Anysola apps
 * spend; **api** is what Anysola Cloud's API keys spend. They follow the same
 * rules — a monthly allowance by plan plus bonus credits that never expire —
 * but never mix: a plan can include one without the other. The api pool is
 * only spent from server to server (`/api/internal/credits/*`).
 */
export const CREDIT_POOLS = ["credits", "api"] as const
export type CreditPool = (typeof CREDIT_POOLS)[number]
export const creditPoolSchema = z.enum(CREDIT_POOLS)

export const CREDIT_POOL_LABELS: Record<CreditPool, string> = {
  credits: "Credits",
  api: "API credits",
}

/** What each plan's API allowance is until an admin sets its own numbers. */
export const DEFAULT_API_ALLOWANCES: Record<BillingPlan, number> = {
  free: 0,
  pro: 100,
  team: 500,
}

export const MAX_CREDIT_ALLOWANCE = 10_000_000
/** The most one call can spend. An app that needs more is doing something wrong. */
export const MAX_CREDIT_SPEND = 100_000
/** The most an admin can add or take away in one adjustment. */
export const MAX_CREDIT_ADJUSTMENT = 1_000_000

// ---------------------------------------------------------------------------
// Apps
// ---------------------------------------------------------------------------

/**
 * An app that may spend credits. The list is data, edited through Account's
 * admin API (`/api/admin/credits/settings/apps`), so a new app doesn't need a
 * deploy.
 *
 * `plans` is where the exceptions live: an app left off a plan can't spend
 * that plan's credits (the terms say so), and `enabled: false` stops an app
 * spending at all — the switch for an app that's misbehaving.
 */
export type CreditApp = {
  id: string
  name: string
  enabled: boolean
  plans: BillingPlan[]
}

export const CREDIT_APP_ID_PATTERN = /^[a-z][a-z0-9-]{1,31}$/

export const creditAppIdSchema = z
  .string()
  .trim()
  .regex(CREDIT_APP_ID_PATTERN, "Use 2–32 lowercase letters, digits or dashes, starting with a letter.")

/**
 * Web's defaults minus `desktop`: Anysola Desktop was removed when the money
 * moved to Account (2026-10-21), so it is not an app that can spend here.
 * `test/account/credits-snapshot.test.ts` pins the rest to web's values.
 */
export const DEFAULT_CREDIT_APPS: CreditApp[] = [
  { id: "chat", name: "Anysola Chat", enabled: true, plans: [...CREDIT_PLANS] },
  { id: "cloud", name: "Anysola Cloud", enabled: true, plans: [...CREDIT_PLANS] },
]

/**
 * A one-time Polar product that adds API credits when paid. The price lives
 * in Polar; only how many credits it's worth is kept here.
 */
export type CreditPack = { productId: string; credits: number }

export const MAX_CREDIT_PACKS = 8
export const MAX_PACK_CREDITS = 10_000_000

export type CreditSettings = {
  allowances: Record<BillingPlan, number>
  apiAllowances: Record<BillingPlan, number>
  apps: CreditApp[]
  apiPacks: CreditPack[]
}

export function defaultCreditSettings(): CreditSettings {
  return {
    allowances: { ...DEFAULT_CREDIT_ALLOWANCES },
    apiAllowances: { ...DEFAULT_API_ALLOWANCES },
    apps: DEFAULT_CREDIT_APPS.map((app) => ({ ...app, plans: [...app.plans] })),
    apiPacks: [],
  }
}

/** The monthly allowance `plan` comes with in `pool`. */
export function poolAllowance(settings: CreditSettings, pool: CreditPool, plan: BillingPlan): number {
  return (pool === "api" ? settings.apiAllowances : settings.allowances)[plan]
}

const POLAR_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function isPolarProductId(value: unknown): value is string {
  return typeof value === "string" && POLAR_ID_PATTERN.test(value)
}

/** Tolerates a stored document from before a plan or an app field existed. */
export function normalizeCreditSettings(stored: Partial<CreditSettings> | null | undefined): CreditSettings {
  const defaults = defaultCreditSettings()
  if (!stored) return defaults
  const readAllowances = (
    values: Partial<Record<BillingPlan, number>> | undefined,
    fallback: Record<BillingPlan, number>
  ) =>
    Object.fromEntries(
      CREDIT_PLANS.map((plan) => {
        const value = values?.[plan]
        return [
          plan,
          typeof value === "number" && Number.isFinite(value) && value >= 0
            ? Math.min(MAX_CREDIT_ALLOWANCE, Math.trunc(value))
            : fallback[plan],
        ]
      })
    ) as Record<BillingPlan, number>
  const allowances = readAllowances(stored.allowances, defaults.allowances)
  const apiAllowances = readAllowances(stored.apiAllowances, defaults.apiAllowances)
  const apps = Array.isArray(stored.apps)
    ? stored.apps
        .filter((app) => app && CREDIT_APP_ID_PATTERN.test(app.id))
        .map((app) => ({
          id: app.id,
          name: typeof app.name === "string" && app.name.trim() ? app.name.trim() : app.id,
          enabled: app.enabled !== false,
          plans: CREDIT_PLANS.filter((plan) => app.plans?.includes(plan)),
        }))
    : defaults.apps
  // A document saved before Anysola Cloud existed has no `cloud` app; the api
  // pool would have no one to spend it otherwise.
  if (!stored.apiAllowances && !apps.some((app) => app.id === "cloud")) {
    apps.push({ ...DEFAULT_CREDIT_APPS.find((app) => app.id === "cloud")!, plans: [...CREDIT_PLANS] })
  }
  const seen = new Set<string>()
  const apiPacks = Array.isArray(stored.apiPacks)
    ? stored.apiPacks
        .filter(
          (pack) =>
            pack &&
            isPolarProductId(pack.productId) &&
            Number.isSafeInteger(pack.credits) &&
            pack.credits > 0 &&
            pack.credits <= MAX_PACK_CREDITS &&
            !seen.has(pack.productId.toLowerCase()) &&
            seen.add(pack.productId.toLowerCase())
        )
        .slice(0, MAX_CREDIT_PACKS)
        .map((pack) => ({ productId: pack.productId.toLowerCase(), credits: pack.credits }))
    : []
  return { allowances, apiAllowances, apps, apiPacks }
}

export type CreditAppDenial = "unknown_app" | "app_disabled" | "plan_excluded"

/** Whether `appId` may spend an account's credits on `plan`, or why not. */
export function creditAppAccess(
  apps: readonly CreditApp[],
  appId: string,
  plan: BillingPlan
): { ok: true; app: CreditApp } | { ok: false; reason: CreditAppDenial } {
  const app = apps.find((candidate) => candidate.id === appId)
  if (!app) return { ok: false, reason: "unknown_app" }
  if (!app.enabled) return { ok: false, reason: "app_disabled" }
  if (!app.plans.includes(plan)) return { ok: false, reason: "plan_excluded" }
  return { ok: true, app }
}

// ---------------------------------------------------------------------------
// Cycles
// ---------------------------------------------------------------------------

export type CreditCycle = {
  /** The cycle's start as an ISO string: stable, and unique per account. */
  key: string
  start: Date
  end: Date
}

/** `date` moved by `months`, clamped to the last day of a shorter month. Time of day is kept. */
export function addMonthsUtc(date: Date, months: number): Date {
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth() + months
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  return new Date(
    Date.UTC(
      year,
      month,
      Math.min(date.getUTCDate(), lastDay),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    )
  )
}

/**
 * The monthly cycle `now` falls in. With an `anchor` — a paid subscription's
 * period end — cycles line up with the day the customer is billed, monthly
 * even on a yearly plan, so credits refresh on the date they'd expect.
 * Without one (the free plan, a granted plan) cycles are calendar months in
 * UTC. Every step is taken from the anchor itself rather than from the last
 * step, so a 31st-of-the-month anchor comes back to the 31st after February.
 */
export function creditCycle(anchor: Date | null, now = new Date()): CreditCycle {
  const base = anchor ?? new Date(Date.UTC(2000, 0, 1))
  let months =
    (now.getUTCFullYear() - base.getUTCFullYear()) * 12 + (now.getUTCMonth() - base.getUTCMonth())
  let start = addMonthsUtc(base, months)
  // At most one step either way: the estimate is off by one when `now` is
  // earlier in its month than the anchor is in its.
  while (start > now) start = addMonthsUtc(base, --months)
  let end = addMonthsUtc(base, months + 1)
  while (end <= now) {
    start = end
    end = addMonthsUtc(base, ++months + 1)
  }
  return { key: start.toISOString(), start, end }
}

/**
 * Which cycle an account is in: billed accounts follow their subscription,
 * everything else follows the calendar. Moving between the two starts a new
 * cycle, which is what makes a new subscription's allowance whole on day one.
 */
export function creditCycleFor(
  entitlement: Pick<EffectiveEntitlement, "source" | "currentPeriodEnd">,
  now = new Date()
): CreditCycle {
  return creditCycle(entitlement.source === "polar" ? entitlement.currentPeriodEnd : null, now)
}

// ---------------------------------------------------------------------------
// Balances
// ---------------------------------------------------------------------------

export type CreditCycleUsage = { key: string; end: Date; used: number }

/**
 * What's stored per account. `cycles` keeps the usage of every cycle that
 * hasn't ended — normally one, but a plan that lapses and comes back within
 * a month returns to its old cycle, and must find what it had already used
 * there. `recent` remembers the latest spends' idempotency keys, so a retry
 * that races the original can't spend twice.
 */
export type CreditAccountState = {
  bonus: number
  cycles: CreditCycleUsage[]
  recent: { key: string; entryId: string }[]
}

export const EMPTY_CREDIT_ACCOUNT: CreditAccountState = { bonus: 0, cycles: [], recent: [] }

/** How many idempotency keys an account row remembers. The ledger remembers the rest. */
export const RECENT_KEYS = 50

export type CreditBalance = {
  /** The plan's monthly amount. */
  allowance: number
  /** Spent from the allowance in this cycle. */
  used: number
  /** What's left of the allowance this cycle. */
  allowanceRemaining: number
  bonus: number
  /** Everything that can be spent now. */
  available: number
}

export function creditBalance(
  state: CreditAccountState,
  cycle: CreditCycle,
  allowance: number
): CreditBalance {
  const used = state.cycles.find((entry) => entry.key === cycle.key)?.used ?? 0
  const allowanceRemaining = Math.max(0, allowance - used)
  const bonus = Math.max(0, state.bonus)
  return { allowance, used, allowanceRemaining, bonus, available: allowanceRemaining + bonus }
}

/**
 * Whether the api pool (Anysola Cloud) belongs on this account's screens: it
 * has a balance or an allowance, was spent from this cycle, has ledger
 * history, or the account holds Cloud keys. One rule for the hub and Billing,
 * so a card never shows on one and not the other. Unknown inputs (`null`, an
 * unread summary) count as nothing.
 */
export function apiPoolInUse({
  apiBalance,
  apiHistoryTotal,
  cloudKeys,
}: {
  apiBalance: Pick<CreditBalance, "allowance" | "available" | "used">
  apiHistoryTotal: number | null
  cloudKeys: number | null
}): boolean {
  return (
    apiBalance.allowance > 0 ||
    apiBalance.available > 0 ||
    apiBalance.used > 0 ||
    (apiHistoryTotal ?? 0) > 0 ||
    (cloudKeys ?? 0) > 0
  )
}

function liveCycles(cycles: readonly CreditCycleUsage[], now: Date) {
  return cycles.filter((entry) => entry.end > now)
}

export type DebitPlan =
  | { ok: true; next: CreditAccountState; fromAllowance: number; fromBonus: number }
  | { ok: false; balance: CreditBalance }

/**
 * Spending `amount`: from the allowance first, bonus second, all or nothing.
 * Returns the state to write, and how the amount was split so a refund can
 * put each part back where it came from.
 */
export function planDebit(
  state: CreditAccountState,
  cycle: CreditCycle,
  allowance: number,
  amount: number,
  now = new Date()
): DebitPlan {
  const balance = creditBalance(state, cycle, allowance)
  if (!Number.isSafeInteger(amount) || amount <= 0 || amount > balance.available) {
    return { ok: false, balance }
  }
  const fromAllowance = Math.min(amount, balance.allowanceRemaining)
  const fromBonus = amount - fromAllowance
  const cycles = liveCycles(state.cycles, now).filter((entry) => entry.key !== cycle.key)
  cycles.push({ key: cycle.key, end: cycle.end, used: balance.used + fromAllowance })
  return {
    ok: true,
    next: { ...state, bonus: balance.bonus - fromBonus, cycles },
    fromAllowance,
    fromBonus,
  }
}

/**
 * Putting a spend back. Bonus always returns; allowance returns only to a
 * cycle that hasn't ended — last month's allowance is gone either way, and
 * handing it back as this month's would let a refund mint credits.
 */
export function planRefund(
  state: CreditAccountState,
  spend: { cycleKey: string; fromAllowance: number; fromBonus: number },
  now = new Date()
): CreditAccountState {
  const cycles = liveCycles(state.cycles, now).map((entry) =>
    entry.key === spend.cycleKey
      ? { ...entry, used: Math.max(0, entry.used - spend.fromAllowance) }
      : entry
  )
  return { ...state, bonus: Math.max(0, state.bonus) + spend.fromBonus, cycles }
}

/**
 * An admin adding (positive) or removing (negative) bonus credits. Removing
 * more than is there stops at zero; the returned `applied` says how much
 * actually moved, which is what the ledger records.
 */
export function planAdjustment(
  state: CreditAccountState,
  delta: number
): { next: CreditAccountState; applied: number } {
  const bonus = Math.max(0, state.bonus)
  const nextBonus = Math.max(0, bonus + delta)
  return { next: { ...state, bonus: nextBonus }, applied: nextBonus - bonus }
}

export function rememberSpend(
  recent: CreditAccountState["recent"],
  key: string,
  entryId: string
): CreditAccountState["recent"] {
  return [...recent.filter((entry) => entry.key !== key), { key, entryId }].slice(-RECENT_KEYS)
}

// ---------------------------------------------------------------------------
// API input
// ---------------------------------------------------------------------------

/**
 * One spend, as an app sends it. `idempotencyKey` is the app's own id for the
 * operation being paid for: sending the same key again — a retry after a
 * timeout — returns the first result instead of spending twice, and it's
 * also the handle for refunding that spend.
 */
export const creditSpendSchema = z.object({
  app: creditAppIdSchema,
  amount: z.number().int().min(1).max(MAX_CREDIT_SPEND),
  idempotencyKey: z
    .string()
    .regex(/^[A-Za-z0-9._:-]{8,128}$/, "8–128 characters: letters, digits and . _ : -"),
  /** Shown to the account holder in their credit history. Keep it short and human. */
  description: z.string().trim().max(120).optional(),
})

export type CreditSpendInput = z.infer<typeof creditSpendSchema>

export const creditRefundSchema = z.object({
  idempotencyKey: creditSpendSchema.shape.idempotencyKey,
})

/**
 * The number of API credits a refunded order takes back: the pack's share
 * that was refunded, rounded up so a partial refund never leaves a fraction
 * of a credit behind. `total` is what the order cost before any refund.
 */
export function revokedPackCredits(credits: number, refunded: number, total: number): number {
  if (refunded <= 0) return 0
  if (total <= 0 || refunded >= total) return credits
  return Math.min(credits, Math.ceil((credits * refunded) / total))
}

/** A rejected body's first problem, as a `400 invalid_request` reports it. */
export function firstRequestIssue(error: z.ZodError): { message: string; field: string | null } {
  const issue = error.issues[0]
  return { message: issue?.message ?? "Invalid body.", field: issue?.path.join(".") || null }
}

/** "1,000". Fixed locale, like every number on the site. */
export function formatCredits(value: number): string {
  return new Intl.NumberFormat("en-US").format(value)
}
